import React, { useState } from 'react';

import {
  AddCircleOutline,
  ArrowDownward,
  ArrowUpward,
  DeleteOutline,
  Edit,
} from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { ImageObject } from '../../pages/types';

interface ImageGalleryEditorProps {
  images?: (string | ImageObject)[];
  rowHeight?: string;
  columns?: number;
  minColumns?: number;
  gap?: string;
  onPropChange: (key: string, value: any) => void;
  openImageSelector: (index: number) => void;
  openImageSelectorMultiple: () => void;
  editImageIndex: number | null;
  setEditImageIndex: (index: number | null) => void;
  handleRemoveImage: (index: number) => void;
  handleUpdateImageMetadata: (
    index: number,
    field: 'title' | 'altText',
    value: string
  ) => void;
  handleMoveImage: (index: number, direction: 'up' | 'down') => void;
  handleMoveToPosition: (currentIndex: number, newPosition: number) => void;
}

const ImageGalleryEditor: React.FC<ImageGalleryEditorProps> = ({
  images = [],
  rowHeight,
  columns = 3,
  minColumns,
  gap = '8px',
  onPropChange,
  openImageSelector,
  openImageSelectorMultiple,
  editImageIndex,
  setEditImageIndex,
  handleRemoveImage,
  handleUpdateImageMetadata,
  handleMoveImage,
  handleMoveToPosition,
}) => {
  const [positionInputs, setPositionInputs] = useState<{
    [key: number]: number;
  }>({});

  const handlePositionChange = (index: number, value: string) => {
    const newPosition = parseInt(value);
    if (!isNaN(newPosition)) {
      setPositionInputs({ ...positionInputs, [index]: newPosition });
    }
  };

  const handlePositionBlur = (index: number) => {
    const newPosition = positionInputs[index];
    if (newPosition !== undefined && !isNaN(newPosition)) {
      handleMoveToPosition(index, newPosition);
      // Reset position inputs after move
      setPositionInputs({});
    }
  };
  // Helper functions for images
  const getImageUrl = (image: string | ImageObject): string => {
    if (typeof image === 'string') return image;
    return image.url;
  };

  const getImageAlt = (image: string | ImageObject): string => {
    if (typeof image === 'string') return `Image`;
    return image.altText || `Image`;
  };

  const getImageFilename = (image: string | ImageObject): string => {
    const url = getImageUrl(image);
    // Extract filename from URL
    const parts = url.split('/');
    const filename = parts[parts.length - 1];
    // Decode URL encoding and truncate if too long
    const decoded = decodeURIComponent(filename);
    return decoded.length > 50 ? decoded.substring(0, 47) + '...' : decoded;
  };

  return (
    <>
      <Typography variant="h6">Content Options</Typography>
      <List sx={{ width: '100%', alignItems: 'left' }}>
        {images.map((image, index) => (
          <ListItem
            key={index}
            component="div"
            sx={{ flexDirection: 'column', alignItems: 'flex-start' }}
          >
            <Box display="flex" width="100%" alignItems="center">
              <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => handleMoveImage(index, 'up')}
                  disabled={index === 0}
                  sx={{ mr: 0.5 }}
                >
                  <ArrowUpward fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleMoveImage(index, 'down')}
                  disabled={index === images.length - 1}
                  sx={{ mr: 1 }}
                >
                  <ArrowDownward fontSize="small" />
                </IconButton>
                <TextField
                  type="number"
                  size="small"
                  sx={{ width: '60px', mr: 1 }}
                  value={
                    positionInputs[index] !== undefined
                      ? positionInputs[index]
                      : index + 1
                  }
                  onChange={(e) => handlePositionChange(index, e.target.value)}
                  onBlur={() => handlePositionBlur(index)}
                  inputProps={{
                    min: 1,
                    max: images.length,
                  }}
                />
              </Box>
              <img
                src={getImageUrl(image)}
                alt={getImageAlt(image)}
                style={{
                  width: '50px',
                  height: '50px',
                  marginRight: '10px',
                  objectFit: 'cover',
                }}
              />
              <Typography
                variant="body1"
                sx={{
                  flex: 1,
                  mr: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {getImageFilename(image)}
              </Typography>
              <Tooltip title="Change Image">
                <IconButton
                  size="small"
                  onClick={() => openImageSelector(index)}
                  color="primary"
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton
                size="small"
                color="secondary"
                onClick={() => handleRemoveImage(index)}
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
              <Button
                variant="outlined"
                size="small"
                onClick={() =>
                  setEditImageIndex(editImageIndex === index ? null : index)
                }
                sx={{ ml: 1 }}
              >
                {editImageIndex === index ? 'Hide' : 'Details'}
              </Button>
            </Box>

            {editImageIndex === index && (
              <Box sx={{ ml: 8, mt: 1, width: '100%' }}>
                <TextField
                  label="Image Title"
                  value={typeof image === 'object' ? image.title || '' : ''}
                  onChange={(e) =>
                    handleUpdateImageMetadata(index, 'title', e.target.value)
                  }
                  fullWidth
                  margin="dense"
                  size="small"
                />
                <TextField
                  label="Alt Text"
                  value={typeof image === 'object' ? image.altText || '' : ''}
                  onChange={(e) =>
                    handleUpdateImageMetadata(index, 'altText', e.target.value)
                  }
                  fullWidth
                  margin="dense"
                  size="small"
                />
              </Box>
            )}
          </ListItem>
        ))}
      </List>
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => openImageSelector(images.length)}
          startIcon={<AddCircleOutline />}
        >
          Add Single Image
        </Button>
        <Button
          variant="outlined"
          color="primary"
          onClick={openImageSelectorMultiple}
          startIcon={<AddCircleOutline />}
        >
          Add Multiple Images
        </Button>
      </Box>
      <Tooltip title="Desktop: full columns, Tablet: half columns, Mobile: 1 column unless overridden">
        <TextField
          label="Columns (Desktop)"
          type="number"
          value={columns}
          onChange={(e) =>
            onPropChange('columns', parseInt(e.target.value) || 3)
          }
          fullWidth
          margin="normal"
          inputProps={{ min: 1, max: 6 }}
        />
      </Tooltip>
      <Tooltip title="Override mobile columns (normally 1). Useful for forcing more columns on mobile.">
        <TextField
          label="Min Columns (Mobile Override)"
          type="number"
          value={minColumns || ''}
          onChange={(e) =>
            onPropChange(
              'minColumns',
              e.target.value ? parseInt(e.target.value) : undefined
            )
          }
          fullWidth
          margin="normal"
          inputProps={{ min: 1, max: 4 }}
        />
      </Tooltip>
      <Tooltip title="Set the height of each row (e.g., 200px, 15vh, auto)">
        <TextField
          label="Row Height"
          value={rowHeight || 'auto'}
          onChange={(e) => onPropChange('rowHeight', e.target.value)}
          fullWidth
          margin="normal"
        />
      </Tooltip>
      <Tooltip title="Spacing between images (e.g., 8px, 1rem, 2px)">
        <TextField
          label="Gap"
          value={gap || '8px'}
          onChange={(e) => onPropChange('gap', e.target.value)}
          fullWidth
          margin="normal"
        />
      </Tooltip>
    </>
  );
};

export default ImageGalleryEditor;
